import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, BACKEND_URL } from "../lib/api";
import AppShell from "../components/AppShell";
import TicketModal from "../components/TicketModal";
import { toast } from "sonner";
import {
  ArrowLeft, FileCsv, FilePdf, UsersThree, Receipt, Plus, Ticket,
  CheckCircle, XCircle, Fingerprint, EnvelopeSimple, Bell, Trash,
  Image as ImageIcon, ShareNetwork, DownloadSimple, UploadSimple, PencilSimple,
} from "@phosphor-icons/react";
const STATUS_STYLE = {
  RED: { text: "text-[#FF0033]", border: "border-[#FF0033]/60", glow: "glow-red", label: "UNPAID" },
  GREEN: { text: "text-[#00FF66]", border: "border-[#00FF66]/60", glow: "glow-green", label: "PAID" },
  PURPLE: { text: "text-[#9D00FF]", border: "border-[#9D00FF]/60", glow: "glow-purple", label: "REDEEMED" },
};

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tickets");
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [issueMember, setIssueMember] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/events/${eventId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const markPaid = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/pay`);
      toast.success("Payment confirmed → GREEN");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm("Delete this ticket? The member's allocation will be returned.")) return;
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success("Ticket deleted; allocation returned");
      setSelectedTicket(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const shareMemberLink = async (mem) => {
    try {
      const { data } = await api.post(`/events/${eventId}/members/${mem.membership_id}/share-link`);
      const url = data.url;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Share link copied for ${mem.member_name}`);
      } catch {
        window.prompt("Share link (copy):", url);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approve = async (requestId) => {
    try {
      await api.post(`/requests/${requestId}/approve`);
      toast.success("Request approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const deny = async (requestId) => {
    try {
      await api.post(`/requests/${requestId}/deny`);
      toast.success("Request denied");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const removeMember = async (membershipId) => {
    if (!window.confirm("Remove this member? Their remaining allocation will return to the event pool.")) return;
    try {
      await api.delete(`/events/${eventId}/members/${membershipId}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadCsv = () => window.open(`${BACKEND_URL}/api/events/${eventId}/journal.csv`, "_blank");
  const downloadPdf = () => window.open(`${BACKEND_URL}/api/events/${eventId}/journal.pdf`, "_blank");
  const [sendingReminder, setSendingReminder] = useState(false);
  const sendReminderNow = async () => {
    const label = window.prompt("Reminder text (when-clause), e.g. 'in 2 hours — see you at the door'", "in a few hours — see you soon!");
    if (!label) return;
    setSendingReminder(true);
    try {
      const res = await api.post(`/events/${eventId}/send-reminders`, { kind: "custom", label });
      toast.success(`Reminder blast sent to ${res.data.sent} · skipped ${res.data.skipped}`);
    } catch (err) {
      toast.error("Reminder failed");
    } finally {
      setSendingReminder(false);
    }
  };

  const publicLink = `${window.location.origin}/e/${eventId}`;

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success("Public link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const uploadPoster = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Max 6MB");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.patch(`/events/${eventId}`, { poster_url: reader.result });
        toast.success("Poster updated");
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    };
    reader.readAsDataURL(file);
  };

  const downloadPoster = () => {
    if (!data?.event?.poster_url) return;
    const a = document.createElement("a");
    a.href = data.event.poster_url;
    a.download = `${data.event.title.replace(/\s+/g, "_")}_poster.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const removePoster = async () => {
    try {
      await api.patch(`/events/${eventId}`, { poster_url: null });
      toast.success("Poster removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm(`Delete "${data?.event?.title}"? This removes all tickets, members and requests. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Event deleted");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="mono-font text-sm text-white/40 py-16 text-center" data-testid="event-detail-loading">
          Loading event…
        </div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell>
        <div className="mono-font text-sm text-white/60 py-16 text-center">Event not found.</div>
      </AppShell>
    );
  }

  const { event, members, tickets, requests } = data;
  const pendingRequests = (requests || []).filter((r) => r.status === "PENDING");
  const pct = event.ticket_cap ? Math.round((event.allocated_count / event.ticket_cap) * 100) : 0;

  return (
    <AppShell>
      <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-white transition-colors duration-200 mb-6">
        <ArrowLeft size={12} /> Back
      </Link>
      <div className="flex items-center justify-end gap-2 mb-4">
        <button onClick={() => setShowEdit(true)} data-testid="edit-event-btn"
          className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-widest text-white/80 hover:border-[#00FF66] hover:text-[#00FF66] transition-colors duration-200">
          <PencilSimple size={12} /> Edit Event
        </button>
        <button onClick={deleteEvent} disabled={deleting} data-testid="delete-event-btn"
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#FF0033]/40 text-[#FF0033] mono-font text-[10px] uppercase tracking-widest hover:bg-[#FF0033] hover:text-white transition-colors duration-200 disabled:opacity-50">
          <Trash size={12} /> {deleting ? "Deleting…" : "Delete Event"}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-white/10 bg-[#0A0A0C] overflow-hidden">
          <div className="aspect-[21/9] bg-black relative">
            {event.design_url ? (
              <img src={event.design_url} alt={event.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full aura-grid flex items-center justify-center">
                <Ticket size={64} weight="duotone" className="text-white/20" />
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">{event.event_id}</div>
            <h1 className="heading-font text-3xl sm:text-4xl font-black tracking-tighter mt-2" data-testid="event-title-heading">
              {event.title}
            </h1>
            {event.description && <p className="text-white/60 mt-3 text-sm">{event.description}</p>}
            {event.host && (
              <div className="mt-3 mono-font text-xs text-white/60" data-testid="event-host-display">
                Hosted by <span className="text-white">{event.host}</span>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-4 mono-font text-xs text-white/60">
              {event.venue && <div>📍 {event.venue}</div>}
              {event.date && <div>🗓 {event.date}</div>}
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-[#0A0A0C] p-6 flex flex-col">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Inventory</div>
          <div className="mt-3 heading-font text-4xl font-black tracking-tighter">
            {event.allocated_count} <span className="text-white/30 text-2xl">/ {event.ticket_cap}</span>
          </div>
          <div className="h-1 bg-white/10 relative overflow-hidden mt-3">
            <div className="absolute inset-y-0 left-0 bg-[#00FF66]" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-6">
            <div>
              <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Issued</div>
              <div className="mono-font text-lg text-white">{event.issued_count}</div>
            </div>
            <div>
              <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Paid</div>
              <div className="mono-font text-lg text-[#00FF66]">{event.paid_count}</div>
            </div>
            <div>
              <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Redeemed</div>
              <div className="mono-font text-lg text-[#9D00FF]">{event.redeemed_count}</div>
            </div>
          </div>
          <div className="mt-auto pt-6">
            <div className="mono-font text-[9px] tracking-[0.3em] uppercase text-white/40 mb-2">Sales Journal</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={downloadCsv} data-testid="export-csv-btn" className="flex items-center justify-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-[0.2em] hover:border-white/60 transition-colors duration-200">
                <FileCsv size={14} /> Journal CSV
              </button>
              <button onClick={downloadPdf} data-testid="export-pdf-btn" className="flex items-center justify-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-[0.2em] hover:border-white/60 transition-colors duration-200">
                <FilePdf size={14} /> Journal PDF
              </button>
            </div>
            <button onClick={sendReminderNow} disabled={sendingReminder} data-testid="send-reminder-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#00FF66]/60 text-[#00FF66] mono-font text-[10px] uppercase tracking-[0.2em] hover:bg-[#00FF66] hover:text-black transition-colors duration-200 disabled:opacity-50">
              <Bell size={12} /> {sendingReminder ? "Sending…" : "Send Reminder Blast"}
            </button>
            <div className="mono-font text-[9px] text-white/30 mt-1">Auto-reminders also fire 24h & 2h before start.</div>
          </div>
        </div>
      </div>

      {/* Poster panel */}
      <div className="border border-white/10 bg-[#0A0A0C] mb-10">
        <div className="p-6 flex flex-col md:flex-row md:items-start gap-6">
          <div className="w-full md:w-64 flex-shrink-0">
            {event.poster_url ? (
              <img src={event.poster_url} alt="poster" className="w-full aspect-[4/5] object-cover border border-white/10" data-testid="event-poster-img" />
            ) : (
              <div className="w-full aspect-[4/5] border border-dashed border-white/15 flex flex-col items-center justify-center text-white/40 gap-2">
                <ImageIcon size={28} weight="duotone" />
                <div className="mono-font text-[10px] tracking-widest uppercase">No poster</div>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 flex items-center gap-2">
              <ImageIcon size={12} /> Public Poster · Advertising
            </div>
            <h2 className="heading-font text-2xl font-black tracking-tighter mt-2">Share your event.</h2>
            <p className="mono-font text-xs text-white/50 mt-2 max-w-xl">
              Upload the poster you designed for social sharing. Anyone with the public link can view the event details.
              Members still control who receives tickets.
            </p>
            <div className="mt-4 flex items-center flex-wrap gap-2 border border-white/10 p-2 max-w-2xl">
              <div className="mono-font text-[10px] tracking-widest uppercase text-white/40 px-2">Link</div>
              <div className="mono-font text-xs text-[#00FF66] truncate flex-1 min-w-0" data-testid="event-public-link">{publicLink}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200" data-testid="event-poster-upload-label">
                <UploadSimple size={14} weight="bold" />
                {event.poster_url ? "Replace Poster" : "Upload Poster"}
                <input type="file" accept="image/*" onChange={uploadPoster} data-testid="event-poster-upload" className="hidden" />
              </label>
              <button onClick={copyPublicLink} data-testid="event-copy-link"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
                <ShareNetwork size={14} /> Copy Link
              </button>
              <a href={publicLink} target="_blank" rel="noreferrer" data-testid="event-open-public"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
                Open Public Page
              </a>
              {event.poster_url && (
                <>
                  <button onClick={downloadPoster} data-testid="event-poster-download"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
                    <DownloadSimple size={14} /> Download Poster
                  </button>
                  <button onClick={removePoster} data-testid="event-poster-remove"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 text-white/50 mono-font text-xs uppercase tracking-[0.25em] hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-colors duration-200">
                    <Trash size={14} /> Remove
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { id: "tickets", label: "Tickets", icon: Receipt, badge: null },
          { id: "members", label: "Members", icon: UsersThree, badge: members.length || null },
          { id: "requests", label: "Requests", icon: Bell, badge: pendingRequests.length || null },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 mono-font text-xs uppercase tracking-[0.25em] border-b-2 transition-colors duration-200 whitespace-nowrap ${
                active ? "text-[#00FF66] border-[#00FF66]" : "text-white/50 border-transparent hover:text-white"
              }`}>
              <Icon size={14} /> {t.label}
              {t.badge ? (
                <span className={`ml-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[9px] tracking-widest ${
                  active ? "bg-[#00FF66] text-black" : "bg-white/10 text-white/80"
                }`}>{t.badge}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Full-width action bar per tab — always visible on mobile */}
      {tab === "members" && (
        <div className="mt-4 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
            {members.length} member{members.length === 1 ? "" : "s"} · Cap {event.ticket_cap - event.allocated_count} tickets remaining
          </div>
          <button onClick={() => setShowInvite(true)} data-testid="invite-member-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
            <Plus size={14} weight="bold" /> Add Members
          </button>
        </div>
      )}
      {tab !== "members" && <div className="mb-6" />}

      {tab === "members" && <MembersTab members={members} onRemove={removeMember} onIssueTicket={setIssueMember} onAdd={() => setShowInvite(true)} onShareLink={shareMemberLink} />}

      {tab === "requests" && (
        <RequestsTab requests={requests || []} onApprove={approve} onDeny={deny} />
      )}

      {tab === "tickets" && (
        <TicketsTab tickets={tickets} onView={setSelectedTicket} onPay={markPaid} onDelete={deleteTicket} onGoIssue={() => setTab("members")} />
      )}

      <InviteMemberDialog open={showInvite} onClose={() => setShowInvite(false)} eventId={eventId}
        remaining={event.ticket_cap - event.allocated_count}
        onDone={() => { setShowInvite(false); load(); }} />
      <EditEventDialog open={showEdit} onClose={() => setShowEdit(false)} event={event}
        onDone={() => { setShowEdit(false); load(); }} />
      <CreatorIssueTicketDialog open={!!issueMember} onClose={() => setIssueMember(null)}
        eventId={eventId} membership={issueMember}
        onDone={() => { setIssueMember(null); load(); }} />
      <TicketModal ticket={selectedTicket} event={event} open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onPay={() => { markPaid(selectedTicket.ticket_id); setSelectedTicket(null); }}
        onDelete={() => deleteTicket(selectedTicket.ticket_id)} />
    </AppShell>
  );
}

function MembersTab({ members, onRemove, onIssueTicket, onAdd, onShareLink }) {
  if (members.length === 0) {
    return (
      <div className="border border-dashed border-white/15 p-12 text-center">
        <UsersThree size={40} weight="duotone" className="mx-auto text-white/40" />
        <div className="heading-font text-xl font-bold mt-4">No members yet</div>
        <div className="mono-font text-xs text-white/50 mt-2">Invite by email, add a roster entry, or bulk-paste a list — then issue tickets on their behalf.</div>
        {onAdd && (
          <button onClick={onAdd} data-testid="members-empty-add-btn"
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
            <Plus size={14} weight="bold" /> Add Your First Member
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="terminal-table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Contact</th>
            <th>Status</th>
            <th>Cap</th>
            <th>Allocated</th>
            <th>Issued</th>
            <th>Available</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const avail = m.total_allocated - m.issued_count;
            const statusColor = m.status === "ACTIVE" ? "text-[#00FF66] border-[#00FF66]/40"
              : m.status === "INVITED" ? "text-[#FF9900] border-[#FF9900]/40"
              : "text-white/60 border-white/20";
            return (
              <tr key={m.membership_id} data-testid={`member-row-${m.membership_id}`}>
                <td className="text-white">{m.member_name}</td>
                <td className="text-white/60">{m.invited_email || m.member_phone || "—"}</td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 text-[10px] tracking-widest border ${statusColor}`}>{m.status}</span>
                </td>
                <td>{m.allocation_cap}</td>
                <td>{m.total_allocated}</td>
                <td>{m.issued_count}</td>
                <td className={avail === 0 ? "text-[#FF0033]" : "text-[#00FF66]"}>{avail}</td>
                <td className="flex items-center gap-2">
                  <button onClick={() => onIssueTicket(m)} data-testid={`creator-issue-${m.membership_id}`}
                    disabled={avail === 0}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#00FF66]/60 text-[#00FF66] mono-font text-[10px] uppercase tracking-widest hover:bg-[#00FF66] hover:text-black transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed">
                    <Plus size={12} /> Issue Ticket
                  </button>
                  <button onClick={() => onShareLink(m)} data-testid={`share-link-${m.membership_id}`}
                    title="Generate & copy share link"
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#9D00FF]/60 text-[#9D00FF] mono-font text-[10px] uppercase tracking-widest hover:bg-[#9D00FF] hover:text-white transition-colors duration-200">
                    <ShareNetwork size={12} /> {m.share_token ? "Copy Link" : "Share Link"}
                  </button>
                  <button onClick={() => onRemove(m.membership_id)} data-testid={`remove-member-${m.membership_id}`}
                    disabled={m.issued_count > 0}
                    className="p-1.5 border border-white/20 mono-font text-white/70 hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed">
                    <Trash size={12} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RequestsTab({ requests, onApprove, onDeny }) {
  if (requests.length === 0) {
    return (
      <div className="border border-dashed border-white/15 p-12 text-center">
        <Bell size={40} weight="duotone" className="mx-auto text-white/40" />
        <div className="heading-font text-xl font-bold mt-4">No requests</div>
        <div className="mono-font text-xs text-white/50 mt-2">Ticket requests from members will appear here.</div>
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="terminal-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Member</th>
            <th>Quantity</th>
            <th>Note</th>
            <th>Status</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => {
            const color = r.status === "PENDING" ? "text-[#FF9900] border-[#FF9900]/40"
                        : r.status === "APPROVED" ? "text-[#00FF66] border-[#00FF66]/40"
                        : "text-[#FF0033] border-[#FF0033]/40";
            return (
              <tr key={r.request_id} data-testid={`request-row-${r.request_id}`}>
                <td className="text-white/60">{r.request_id.slice(0, 10)}</td>
                <td>{r.member_name} <span className="text-white/40">· {r.member_email}</span></td>
                <td className="text-white">+{r.quantity}</td>
                <td className="text-white/70 max-w-xs truncate">{r.note || "—"}</td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 text-[10px] tracking-widest border ${color}`}>{r.status}</span>
                </td>
                <td className="text-white/60">{new Date(r.created_at).toLocaleString()}</td>
                <td>
                  {r.status === "PENDING" ? (
                    <div className="flex gap-2">
                      <button onClick={() => onApprove(r.request_id)} data-testid={`approve-request-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#00FF66]/60 text-[#00FF66] mono-font text-[10px] uppercase tracking-widest hover:bg-[#00FF66] hover:text-black transition-colors duration-200">
                        <CheckCircle size={12} /> Approve
                      </button>
                      <button onClick={() => onDeny(r.request_id)} data-testid={`deny-request-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#FF0033]/60 text-[#FF0033] mono-font text-[10px] uppercase tracking-widest hover:bg-[#FF0033] hover:text-white transition-colors duration-200">
                        <XCircle size={12} /> Deny
                      </button>
                    </div>
                  ) : (
                    <div className="mono-font text-[10px] text-white/40 uppercase tracking-widest">Resolved</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TicketsTab({ tickets, onView, onPay, onDelete, onGoIssue }) {
  if (tickets.length === 0) {
    return (
      <div className="border border-dashed border-white/15 p-12 text-center">
        <Receipt size={40} weight="duotone" className="mx-auto text-white/40" />
        <div className="heading-font text-xl font-bold mt-4">No tickets issued yet</div>
        <div className="mono-font text-xs text-white/50 mt-2">Add a member from the <span className="text-white">Members</span> tab, then click <span className="text-[#00FF66]">Issue Ticket</span>.</div>
        {onGoIssue && (
          <button onClick={onGoIssue} data-testid="tickets-empty-goto-members"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
            <UsersThree size={14} /> Manage Members
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="terminal-table">
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Buyer</th>
            <th>Phone</th>
            <th>Member</th>
            <th>Status</th>
            <th>Checksum</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => {
            const s = STATUS_STYLE[t.color_code];
            return (
              <tr key={t.ticket_id} data-testid={`ticket-row-${t.ticket_id}`}>
                <td className="text-white">{t.ticket_id}</td>
                <td>{t.buyer_name}</td>
                <td className="text-white/60">{t.buyer_phone}</td>
                <td className="text-white/60">{t.member_name}</td>
                <td>
                  <span className={`inline-flex items-center gap-2 px-2 py-1 border ${s.border} ${s.text} ${s.glow} text-[10px] tracking-widest`}>
                    <Fingerprint size={12} weight="fill" /> {t.color_code} · {s.label}
                  </span>
                </td>
                <td className="text-white/60">{t.token_hash}</td>
                <td className="flex items-center gap-2">
                  <button onClick={() => onView(t)} data-testid={`view-ticket-${t.ticket_id}`}
                    className="px-3 py-1.5 border border-white/20 mono-font text-[10px] uppercase tracking-widest hover:border-white/60 transition-colors duration-200">
                    View
                  </button>
                  {t.color_code === "RED" && (
                    <button onClick={() => onPay(t.ticket_id)} data-testid={`pay-ticket-${t.ticket_id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#00FF66]/60 text-[#00FF66] mono-font text-[10px] uppercase tracking-widest hover:bg-[#00FF66] hover:text-black transition-colors duration-200">
                      <CheckCircle size={12} /> Mark Paid
                    </button>
                  )}
                  {t.color_code !== "PURPLE" && onDelete && (
                    <button onClick={() => onDelete(t.ticket_id)} data-testid={`delete-ticket-${t.ticket_id}`}
                      className="p-1.5 border border-white/20 mono-font text-white/60 hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-colors duration-200"
                      title="Delete ticket">
                      <Trash size={12} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InviteMemberDialog({ open, onClose, eventId, remaining, onDone }) {
  const [mode, setMode] = useState("invite"); // invite | roster | bulk
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cap, setCap] = useState(50);
  const [initial, setInitial] = useState(0);
  const [sendInvite, setSendInvite] = useState(true);
  const [bulkText, setBulkText] = useState("Alice,alice@example.com,50,5\nBob,,20,0");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submitSingle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        name,
        email: mode === "roster" ? null : (email || null),
        phone: phone || null,
        allocation_cap: Number(cap),
        initial_allocation: Number(initial),
        send_invite: mode === "invite" && sendInvite,
      };
      await api.post(`/events/${eventId}/members`, body);
      toast.success(mode === "roster" ? "Roster entry added" : `Invited ${email}`);
      setName(""); setEmail(""); setPhone(""); setCap(50); setInitial(0);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    const rows = bulkText.split("\n").map((r) => r.trim()).filter(Boolean).map((r) => {
      const parts = r.split(",").map((x) => x.trim());
      return {
        name: parts[0],
        email: parts[1] || null,
        allocation_cap: Number(parts[2] || 50),
        initial_allocation: Number(parts[3] || 0),
      };
    }).filter((r) => r.name);
    if (rows.length === 0) return toast.error("No rows parsed");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${eventId}/members/bulk`, { members: rows, send_invite: sendInvite });
      toast.success(`Added ${data.created_count} · Skipped ${data.skipped_count}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0A0A0C] border border-white/15 p-6 max-h-[95vh] overflow-auto">
        <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Event Member Management</div>
        <div className="heading-font text-2xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <EnvelopeSimple size={22} weight="duotone" className="text-[#00FF66]" /> Add Members
        </div>
        <div className="mono-font text-xs text-white/50 mt-2">Remaining event capacity: <span className="text-[#00FF66]">{remaining}</span></div>

        <div className="mt-4 flex gap-1 border-b border-white/10">
          {[
            { id: "invite", label: "Invite by email" },
            { id: "roster", label: "Roster only" },
            { id: "bulk", label: "Bulk paste" },
          ].map((t) => (
            <button key={t.id} onClick={() => setMode(t.id)} data-testid={`invite-mode-${t.id}`}
              className={`px-3 py-2 mono-font text-[10px] uppercase tracking-widest border-b-2 transition-colors duration-200 ${
                mode === t.id ? "text-[#00FF66] border-[#00FF66]" : "text-white/50 border-transparent hover:text-white"
              }`}>{t.label}</button>
          ))}
        </div>

        {mode === "bulk" ? (
          <form onSubmit={submitBulk} className="mt-6 space-y-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Paste CSV: Name,Email,Cap,Initial</label>
              <textarea rows={8} value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="invite-bulk-text"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-xs focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
              <div className="mono-font text-[9px] text-white/40 mt-1">One per line. Email optional (blank = roster entry).</div>
            </div>
            <label className="flex items-center gap-2 mono-font text-xs text-white/70">
              <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="invite-send-invite-bulk" />
              Send invite emails to members with an email
            </label>
            <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="invite-bulk-submit"
                className="px-5 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
                {submitting ? "Adding…" : "Add All"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitSingle} className="mt-6 space-y-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Name</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
            {mode === "invite" && (
              <div>
                <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Email</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email"
                  className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                  placeholder="member@example.com" />
              </div>
            )}
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Phone (optional)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="invite-phone"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Max Allocation</label>
                <input required type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} data-testid="invite-cap"
                  className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
              </div>
              <div>
                <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Initial Grant</label>
                <input required type="number" min={0} max={Math.min(Number(cap) || 0, remaining)}
                  value={initial} onChange={(e) => setInitial(e.target.value)} data-testid="invite-initial"
                  className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
              </div>
            </div>
            {mode === "invite" && (
              <label className="flex items-center gap-2 mono-font text-xs text-white/70">
                <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="invite-send-invite" />
                Send invite email
              </label>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
              <button type="button" onClick={onClose} data-testid="invite-cancel" className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="invite-submit"
                className="px-5 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
                {submitting ? "Saving…" : mode === "roster" ? "Add Roster" : "Send Invite"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function CreatorIssueTicketDialog({ open, onClose, eventId, membership, onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!open || !membership) return null;
  const avail = membership.total_allocated - membership.issued_count;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/events/${eventId}/members/${membership.membership_id}/tickets`,
        { buyer_name: name, buyer_phone: phone });
      toast.success(`Ticket issued via ${membership.member_name}`);
      setName(""); setPhone("");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0A0A0C] border border-white/15 p-6">
        <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Creator · Issue Ticket</div>
        <div className="heading-font text-2xl font-black tracking-tighter mt-1">Distribute Ticket</div>
        <div className="mono-font text-xs text-white/50 mt-2">
          Credited to <span className="text-white">{membership.member_name}</span> · Available: <span className={avail === 0 ? "text-[#FF0033]" : "text-[#00FF66]"}>{avail}</span>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Buyer Name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="creator-issue-name"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Buyer Phone</label>
            <input required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="creator-issue-phone"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">Cancel</button>
            <button type="submit" disabled={submitting || avail === 0} data-testid="creator-issue-submit"
              className="px-5 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
              {submitting ? "Issuing…" : "Issue Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



function EditEventDialog({ open, onClose, event, onDone }) {
  const [title, setTitle] = useState("");
  const [host, setHost] = useState("");
  const [venue, setVenue] = useState("");
  const [date, setDate] = useState("");
  const [description, setDescription] = useState("");
  const [ticketCap, setTicketCap] = useState(0);
  const [status, setStatus] = useState("ACTIVE");
  const [designUrl, setDesignUrl] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterAspect, setPosterAspect] = useState("free"); // free | 1:1 | 4:5
  const [aiProvider, setAiProvider] = useState("gemini");
  const [artStyle, setArtStyle] = useState("");
  const [generatingAi, setGeneratingAi] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (event) {
      setTitle(event.title || "");
      setHost(event.host || event.creator_name || "");
      setVenue(event.venue || "");
      setDate(event.date || "");
      setDescription(event.description || "");
      setTicketCap(event.ticket_cap || 0);
      setStatus(event.status || "ACTIVE");
      setDesignUrl(event.design_url || null);
      setPosterUrl(event.poster_url || null);
      setArtStyle(event.art_style || "");
      setPosterAspect("free");
    }
  }, [event, open]);

  if (!open || !event) return null;

  const minCap = event.allocated_count || 0;

  const readFile = (file, maxMB) =>
    new Promise((resolve, reject) => {
      if (file.size > maxMB * 1024 * 1024) return reject(new Error(`Max ${maxMB}MB`));
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const cropToAspect = (dataUrl, aspect) =>
    new Promise((resolve, reject) => {
      if (aspect === "free") return resolve(dataUrl);
      const [aw, ah] = aspect.split(":").map(Number);
      const targetRatio = aw / ah;
      const img = new Image();
      img.onload = () => {
        const srcRatio = img.width / img.height;
        let sw = img.width, sh = img.height, sx = 0, sy = 0;
        if (srcRatio > targetRatio) {
          // too wide → crop sides
          sw = img.height * targetRatio;
          sx = (img.width - sw) / 2;
        } else {
          // too tall → crop top/bottom
          sh = img.width / targetRatio;
          sy = (img.height - sh) / 2;
        }
        const canvas = document.createElement("canvas");
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });

  const onPickDesign = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setDesignUrl(await readFile(f, 3)); } catch (err) { toast.error(err.message); }
  };
  const onPickPoster = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const raw = await readFile(f, 6);
      const cropped = await cropToAspect(raw, posterAspect);
      setPosterUrl(cropped);
    } catch (err) { toast.error(err.message); }
  };
  const reCropPoster = async (nextAspect) => {
    setPosterAspect(nextAspect);
    if (!posterUrl) return;
    try {
      const cropped = await cropToAspect(posterUrl, nextAspect);
      setPosterUrl(cropped);
    } catch (err) { toast.error("Re-crop failed"); }
  };
  const regenerateAi = async () => {
    if (!title.trim()) return toast.error("Title required to regenerate art");
    setGeneratingAi(true);
    try {
      const res = await api.post("/ai/ticket-design", { title, model_provider: aiProvider, art_style: artStyle });
      setDesignUrl(res.data.design_url);
      toast.success("Ticket art regenerated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI generation failed");
    } finally {
      setGeneratingAi(false);
    }
  };
  const notifyBearers = async () => {
    setNotifying(true);
    try {
      const res = await api.post(`/events/${event.event_id}/notify-update`, {
        note: "The poster and/or ticket art has been refreshed. Please re-download your ticket.",
      });
      toast.success(`Notified ${res.data.sent} bearer(s) · skipped ${res.data.skipped}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Notify failed");
    } finally {
      setNotifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (Number(ticketCap) < minCap) {
      return toast.error(`Ticket cap cannot be lower than ${minCap} (already allocated)`);
    }
    setSubmitting(true);
    try {
      await api.patch(`/events/${event.event_id}`, {
        title,
        host,
        venue,
        date: date || null,
        description,
        ticket_cap: Number(ticketCap),
        status,
        design_url: designUrl,
        poster_url: posterUrl,
        art_style: artStyle,
      });
      toast.success("Event updated");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start md:items-center justify-center p-4 overflow-auto" data-testid="edit-event-dialog">
      <div className="w-full max-w-2xl bg-[#0A0A0C] border border-white/15 my-8">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Event Orchestration Agent</div>
            <div className="heading-font text-2xl font-black tracking-tighter mt-1">Edit Event</div>
          </div>
          <button onClick={onClose} data-testid="edit-event-close" className="p-2 hover:bg-white/5 transition-colors duration-200">
            <XCircle size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Title</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-event-title"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Host</label>
            <input value={host} onChange={(e) => setHost(e.target.value)} data-testid="edit-event-host"
              placeholder="Person / brand hosting this event"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            <div className="mono-font text-[9px] text-white/40 mt-1">Leave blank to fall back to your name.</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Venue</label>
              <input value={venue} onChange={(e) => setVenue(e.target.value)} data-testid="edit-event-venue"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-event-date"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
          </div>
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Description</label>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="edit-event-description"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Ticket Art (appears on ticket & banner)</label>
              <div className="mt-2 border border-white/10 p-3 flex items-start gap-3">
                <div className="w-24 h-24 flex-shrink-0 bg-black border border-white/10 overflow-hidden">
                  {designUrl ? (
                    <img src={designUrl} alt="design" className="w-full h-full object-cover" data-testid="edit-design-preview" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center mono-font text-[9px] text-white/30 uppercase">None</div>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-widest hover:border-[#00FF66] transition-colors duration-200">
                    <UploadSimple size={12} weight="bold" /> {designUrl ? "Replace" : "Upload"}
                    <input type="file" accept="image/*" onChange={onPickDesign} data-testid="edit-design-upload" className="hidden" />
                  </label>
                  {designUrl && (
                    <button type="button" onClick={() => setDesignUrl(null)} data-testid="edit-design-remove"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/10 mono-font text-[10px] uppercase tracking-widest text-white/60 hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-colors duration-200">
                      <Trash size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
              {/* AI regenerate row */}
              <div className="mt-2 border border-white/10 p-3">
                <div className="mono-font text-[9px] uppercase tracking-[0.25em] text-white/50 mb-2">Regenerate with AI</div>
                <input value={artStyle} onChange={(e) => setArtStyle(e.target.value)} data-testid="edit-art-style"
                  placeholder="Style prompt · e.g. cyberpunk, neon minimal, vintage 90s"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 mono-font text-[11px] mb-2 focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
                <div className="flex items-center gap-2">
                  <select value={aiProvider} onChange={(e) => setAiProvider(e.target.value)} data-testid="edit-ai-provider"
                    className="bg-[#050505] border border-white/15 px-3 py-2 mono-font text-[11px] focus:outline-none focus:border-[#00FF66]">
                    <option value="gemini">Nano Banana</option>
                    <option value="openai">GPT Image 1</option>
                  </select>
                  <button type="button" onClick={regenerateAi} disabled={generatingAi} data-testid="edit-ai-regenerate"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#00FF66]/60 text-[#00FF66] mono-font text-[10px] uppercase tracking-widest hover:bg-[#00FF66] hover:text-black transition-colors duration-200 disabled:opacity-50">
                    {generatingAi ? "Generating…" : "Regenerate Art"}
                  </button>
                </div>
                <div className="mono-font text-[9px] text-white/40 mt-2">Style is saved to the event so re-runs stay consistent.</div>
              </div>
              <div className="mono-font text-[9px] text-white/40 mt-1">Max 3MB upload. AI uses current title.</div>
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Poster (social sharing)</label>
              <div className="mt-2 border border-white/10 p-3 flex items-start gap-3">
                <div className="w-24 h-24 flex-shrink-0 bg-black border border-white/10 overflow-hidden">
                  {posterUrl ? (
                    <img src={posterUrl} alt="poster" className="w-full h-full object-cover" data-testid="edit-poster-preview" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center mono-font text-[9px] text-white/30 uppercase">None</div>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-widest hover:border-[#00FF66] transition-colors duration-200">
                    <UploadSimple size={12} weight="bold" /> {posterUrl ? "Replace" : "Upload"}
                    <input type="file" accept="image/*" onChange={onPickPoster} data-testid="edit-poster-upload" className="hidden" />
                  </label>
                  {posterUrl && (
                    <button type="button" onClick={() => setPosterUrl(null)} data-testid="edit-poster-remove"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/10 mono-font text-[10px] uppercase tracking-widest text-white/60 hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-colors duration-200">
                      <Trash size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
              {/* Aspect ratio crop toggle */}
              <div className="mt-2 border border-white/10 p-3">
                <div className="mono-font text-[9px] uppercase tracking-[0.25em] text-white/50 mb-2">Aspect Ratio</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "free", label: "Free" },
                    { key: "1:1", label: "1:1 · Feed" },
                    { key: "4:5", label: "4:5 · Portrait" },
                  ].map((opt) => (
                    <button key={opt.key} type="button" onClick={() => reCropPoster(opt.key)}
                      data-testid={`poster-aspect-${opt.key}`}
                      className={`px-2 py-2 mono-font text-[9px] uppercase tracking-widest border transition-colors duration-200 ${
                        posterAspect === opt.key
                          ? "border-[#00FF66] text-[#00FF66] bg-[#00FF66]/10"
                          : "border-white/10 text-white/60 hover:border-white/30"
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mono-font text-[9px] text-white/40 mt-1">Max 6MB. Center-crops on aspect change.</div>
            </div>
          </div>

          {/* Live Ticket Preview */}
          <div className="border border-white/10 p-4" data-testid="ticket-live-preview">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Live Ticket Preview</div>
            <div className="flex overflow-hidden border border-white/10" style={{ aspectRatio: "16/7" }}>
              {/* Art panel */}
              <div className="relative flex-1 bg-black overflow-hidden">
                {designUrl ? (
                  <img src={designUrl} alt="ticket art" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00FF66]/20 via-black to-[#9D00FF]/20" />
                )}
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative p-4 h-full flex flex-col justify-between">
                  <div>
                    <div className="mono-font text-[8px] uppercase tracking-[0.3em] text-[#00FF66]">AURA · Event</div>
                    <div className="heading-font text-lg font-bold text-white leading-tight mt-1 line-clamp-2">
                      {title || "Untitled Event"}
                    </div>
                  </div>
                  <div className="mono-font text-[9px] text-white/70">
                    {host ? `Hosted by ${host}` : "\u00a0"}
                  </div>
                </div>
              </div>
              {/* Perforation */}
              <div className="w-2 bg-black border-l border-r border-white/10 border-dashed" />
              {/* Stub */}
              <div className="w-[40%] bg-[#0A0A0C] p-3 flex flex-col">
                <div className="bg-[#FF0033] text-black text-center mono-font font-bold text-xs py-1 mb-2">UNPAID</div>
                <div className="mono-font text-[8px] uppercase tracking-widest text-white/50">Bearer</div>
                <div className="mono-font text-xs text-white truncate">Guest Preview</div>
                <div className="mt-auto flex items-end justify-between">
                  <div>
                    <div className="mono-font text-[8px] uppercase tracking-widest text-white/50">Ticket</div>
                    <div className="mono-font text-[10px] text-white">AU-XXXX-000</div>
                  </div>
                  <div className="w-10 h-10 bg-white border border-white/20" />
                </div>
              </div>
            </div>
            <div className="mono-font text-[9px] text-white/40 mt-2">Mockup only — download actual PNG/PDF after saving.</div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Ticket Cap</label>
              <input required type="number" min={minCap || 1} value={ticketCap}
                onChange={(e) => setTicketCap(e.target.value)} data-testid="edit-event-cap"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
              <div className="mono-font text-[9px] text-white/40 mt-1">
                Minimum allowed: {minCap} (already allocated)
              </div>
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="edit-event-status"
                className="mt-2 w-full bg-[#050505] border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200">
                <option value="ACTIVE">Active</option>
                <option value="DRAFT">Draft</option>
                <option value="PAUSED">Paused</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
            <button type="button" onClick={notifyBearers} disabled={notifying} data-testid="edit-notify-bearers"
              className="mr-auto px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-[#00FF66] hover:text-[#00FF66] transition-colors duration-200 disabled:opacity-50">
              {notifying ? "Notifying…" : "Notify Bearers"}
            </button>
            <button type="button" onClick={onClose} data-testid="edit-event-cancel"
              className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting} data-testid="edit-event-submit"
              className="px-5 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
              {submitting ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
